"use client";

import { signOut } from "next-auth/react";
import { usePrivy } from "@privy-io/react-auth";
import { usePrivyAvailable } from "@/components/WalletConnectProvider";
import { clientLogger } from "@/lib/client-logger";

interface SignOutButtonProps {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  /** Runs before sign-out begins, e.g. to close an open menu. */
  onBeforeSignOut?: () => void;
}

/**
 * Signs the user out of BOTH Privy and NextAuth, then returns home.
 *
 * Privy must be logged out FIRST. NextAuth sessions are re-created from a live
 * Privy session by PrivyNextAuthBridge, so clearing only NextAuth (and then
 * reloading) immediately logs the user back in. Logging out of Privy first
 * means `authenticated` is false by the time NextAuth clears, leaving the
 * bridge nothing to re-authenticate from.
 *
 * Renders the Privy-aware variant only when Privy is mounted; otherwise a plain
 * NextAuth sign-out (so it's safe on origins where PrivyProvider isn't present).
 */
export function SignOutButton(props: SignOutButtonProps) {
  const privyAvailable = usePrivyAvailable();
  return privyAvailable ? <PrivyAwareSignOut {...props} /> : <PlainSignOut {...props} />;
}

function PrivyAwareSignOut({ className, style, children, onBeforeSignOut }: SignOutButtonProps) {
  const { logout } = usePrivy();
  return (
    <button
      type="button"
      className={className}
      style={style}
      onClick={async () => {
        onBeforeSignOut?.();
        try {
          await logout();
        } catch (err) {
          clientLogger.warn("Privy logout during sign-out failed", {
            error: err instanceof Error ? err.message : "Unknown",
          });
        }
        await signOut({ redirect: false });
        window.location.href = "/";
      }}
    >
      {children}
    </button>
  );
}

function PlainSignOut({ className, style, children, onBeforeSignOut }: SignOutButtonProps) {
  return (
    <button
      type="button"
      className={className}
      style={style}
      onClick={async () => {
        onBeforeSignOut?.();
        await signOut({ redirect: false });
        window.location.href = "/";
      }}
    >
      {children}
    </button>
  );
}
